import { Handshake, Users, Star, Megaphone, Mail, Check, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { Link } from "react-router-dom";

const partnershipOptions = [
  {
    icon: Users,
    title: "Affiliate Partnership",
    description: "Join our affiliate program and earn competitive commissions by promoting trusted online casinos to your audience.",
    features: ["Competitive commission rates", "Real-time tracking", "Dedicated support", "Marketing materials"],
  },
  {
    icon: Megaphone,
    title: "Advertising",
    description: "Reach thousands of active casino players with premium advertising placements across our platform.",
    features: ["Banner placements", "Sponsored content", "Newsletter inclusion", "Social media promotion"],
  },
  {
    icon: Star,
    title: "Featured Listings",
    description: "Get your casino prominently featured in our top rankings and recommendation sections.",
    features: ["Priority placement", "Enhanced visibility", "Badge recognition", "Monthly reports"],
  },
  {
    icon: Handshake,
    title: "Sponsored Reviews",
    description: "Work with our team to create comprehensive, transparent reviews that showcase your casino's strengths.",
    features: ["In-depth coverage", "Fair assessment", "SEO optimized", "Social amplification"],
  },
];

export default function Partnership() {
  return (
    <div className="min-h-screen py-8" data-testid="partnership-page">
      {/* Header */}
      <section className="gradient-hero py-16 mb-8">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass text-secondary text-sm font-medium mb-6">
            <Handshake className="w-4 h-4" />
            <span>Grow Together</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-black text-white mb-4 font-['Montserrat']">
            Partner with <span className="text-primary">PlaySpiro</span>
          </h1>
          <p className="text-white/60 max-w-2xl mx-auto text-lg">
            Join forces with one of the leading casino review platforms. 
            Reach engaged players and grow your business.
          </p>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4">
        {/* Partnership Options */}
        <div className="grid md:grid-cols-2 gap-6 mb-16">
          {partnershipOptions.map((option, index) => (
            <div 
              key={index}
              className="glass rounded-2xl p-6 md:p-8 card-hover"
            >
              <div className="w-14 h-14 rounded-xl bg-primary/20 flex items-center justify-center mb-4">
                <option.icon className="w-7 h-7 text-primary" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-3 font-['Montserrat']">{option.title}</h3>
              <p className="text-white/60 mb-6">{option.description}</p>
              <ul className="space-y-2">
                {option.features.map((feature, i) => (
                  <li key={i} className="flex items-center gap-2 text-white/70">
                    <Check className="w-4 h-4 text-green-400" />
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Why Partner With Us */}
        <section className="glass rounded-2xl p-8 md:p-12 mb-16">
          <h2 className="text-3xl font-bold text-white mb-8 text-center font-['Montserrat']">
            Why Partner With PlaySpiro?
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="text-4xl font-black text-secondary mb-2">50K+</div>
              <p className="text-white/60">Monthly Visitors</p>
            </div>
            <div className="text-center">
              <div className="text-4xl font-black text-primary mb-2">10K+</div>
              <p className="text-white/60">Active Players</p>
            </div>
            <div className="text-center">
              <div className="text-4xl font-black text-green-400 mb-2">95%</div>
              <p className="text-white/60">Partner Satisfaction</p>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="text-center mb-16">
          <div className="glass rounded-2xl p-8 md:p-12 relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-primary/10 via-transparent to-secondary/10" />
            <div className="relative">
              <Mail className="w-12 h-12 text-secondary mx-auto mb-4" />
              <h2 className="text-3xl font-bold text-white mb-4 font-['Montserrat']">
                Ready to Partner?
              </h2>
              <p className="text-white/60 mb-8 max-w-xl mx-auto">
                Get in touch with our partnership team to discuss how we can work together.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href="mailto:partner@playspiro.com">
                  <Button 
                    size="lg" 
                    className="btn-glow gradient-neon text-white font-bold rounded-full px-8"
                    data-testid="partner-email-btn"
                  >
                    <Mail className="w-5 h-5 mr-2" /> partner@playspiro.com
                  </Button>
                </a>
                <Link to="/contact">
                  <Button 
                    size="lg" 
                    variant="outline" 
                    className="border-white/20 text-white hover:bg-white/10 rounded-full px-8"
                  >
                    Contact Form <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
